import { useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Plane, ClipboardCheck, ArrowLeft } from '@/components/icons'
import { PrntrGroup, PrntrPickerRow } from '@/components/ui/prntr-input'
import { AircraftPickerDrawer } from '@/components/AircraftPickerDrawer'
import { CrewMemberPickerDrawer } from '@/components/CrewMemberPickerDrawer'
import { ChecklistPickerDrawer } from '@/components/ChecklistPickerDrawer'
import {
  useAircraftGetAll,
  useCrewGetAll,
  useCrewGetMe,
  useChecklistTemplates,
  useTaskStart,
} from '@/lib/hooks'
import { cn, getInitials, formatCrewRole } from '@/lib/utils'
import { hapticLight } from '@/lib/haptics'

export const Route = createFileRoute('/_auth/tasks/start')({
  component: StartTaskRoute,
})

function StartTaskRoute() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const startTask = useTaskStart()

  const { data: me } = useCrewGetMe()
  const { data: allAircraft = [] } = useAircraftGetAll()
  const { data: allCrew = [] } = useCrewGetAll()
  const { data: templates = [] } = useChecklistTemplates()

  const [templateId, setTemplateId] = useState<string | undefined>()
  const [aircraftOn, setAircraftOn] = useState(false)
  const [aircraftId, setAircraftId] = useState<string | undefined>()
  const [crewOn, setCrewOn] = useState(false)
  const [crewId, setCrewId] = useState<string | undefined>()

  const [checklistOpen, setChecklistOpen] = useState(false)
  const [aircraftPickerOpen, setAircraftPickerOpen] = useState(false)
  const [crewPickerOpen, setCrewPickerOpen] = useState(false)

  const selectedTemplate = templates.find((t) => t.id === templateId)
  const selectedAircraft = allAircraft.find((a) => a.id === aircraftId)
  // When the crew switch is on but nothing is picked, the assignee defaults to the current user.
  const effectiveCrew = crewId ? allCrew.find((c) => c.id === crewId) : crewOn ? me : undefined

  const hasChecklist = !!templateId
  const isPending = startTask.isPending

  // The task is assigned to the current crew when the crew switch is off, no
  // crew is picked (defaults to you), or the picked crew is you. Only then can
  // it be started right away.
  const isSelfAssignment = !crewOn || !crewId || crewId === me?.id

  const handleAircraftToggle = (checked: boolean) => {
    hapticLight()
    setAircraftOn(checked)
    if (checked) {
      if (!aircraftId) setAircraftPickerOpen(true)
    } else {
      setAircraftId(undefined)
    }
  }

  const handleCrewToggle = (checked: boolean) => {
    hapticLight()
    setCrewOn(checked)
    if (!checked) setCrewId(undefined)
  }

  const baseInput = () => ({
    checklistTemplateId: templateId!,
    aircraftId: aircraftOn ? aircraftId ?? null : null,
  })

  // "Start Task": create assigned to self and open the task to begin now.
  const handleStartTask = async () => {
    if (!hasChecklist || isPending) return
    try {
      const task = await startTask.mutateAsync({
        ...baseInput(),
        assignedCrewId: crewId ?? null, // backend backfills the current user
      })
      navigate({ to: '/task/$taskId', params: { taskId: task.id } })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start task')
    }
  }

  // "Assign Task": create (possibly for another crew member) and return to the hub.
  const handleAssignTask = async () => {
    if (!hasChecklist || isPending) return
    try {
      await startTask.mutateAsync({
        ...baseInput(),
        assignedCrewId: crewOn ? crewId ?? me?.id ?? null : null,
      })
      navigate({ to: '/tasks' })
      toast.success('Task assigned')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign task')
    }
  }

  const crewName = effectiveCrew
    ? effectiveCrew.firstName && effectiveCrew.lastName
      ? `${effectiveCrew.firstName} ${effectiveCrew.lastName}`
      : effectiveCrew.name
    : null

  const body = (
    <>
      <div
        className={cn('flex flex-col gap-6', !isDesktop && 'pb-24')}
      >
        <p className="text-sm text-content-primary-bold leading-[1.5]">
          Start a task or assign a task to aircraft, crew members or both.
        </p>

        <div className="flex flex-col gap-3">
          {/* Task / checklist */}
          <Card className="flex flex-col gap-1">
            <PrntrGroup>
              <PrntrPickerRow
                label="Task"
                iconLeft={<ClipboardCheck />}
                value={selectedTemplate?.name ?? null}
                placeholder="Select task"
                onClick={() => setChecklistOpen(true)}
                onClear={selectedTemplate ? () => setTemplateId(undefined) : undefined}
              />
            </PrntrGroup>
            <p className="text-xs text-content-primary-subtle leading-[1.5]">
              Select a checklist for your created lists.
            </p>
          </Card>

          {/* Assign to Aircraft */}
          <RichSwitchGroup
            title="Assign to Aircraft?"
            caption="Select an aircraft for handover reports etc."
            checked={aircraftOn}
            onCheckedChange={handleAircraftToggle}
          >
            <PrntrGroup>
              <PrntrPickerRow
                label="Aircraft"
                iconLeft={<Plane />}
                value={selectedAircraft?.registration ?? null}
                placeholder="Select aircraft"
                onClick={() => setAircraftPickerOpen(true)}
                onClear={selectedAircraft ? () => setAircraftId(undefined) : undefined}
              />
            </PrntrGroup>
          </RichSwitchGroup>

          {/* Assign to Crew Member */}
          <RichSwitchGroup
            title="Assign to Crew Member?"
            caption="Crew will default to you if none selected."
            checked={crewOn}
            onCheckedChange={handleCrewToggle}
          >
            <PrntrGroup>
              <PrntrPickerRow
                label="Crew"
                iconLeft={
                  effectiveCrew ? (
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback
                        className="text-background text-sm"
                        style={
                          effectiveCrew.avatarColor
                            ? { backgroundColor: effectiveCrew.avatarColor }
                            : undefined
                        }
                      >
                        {getInitials(effectiveCrew.firstName, effectiveCrew.lastName)}
                      </AvatarFallback>
                    </Avatar>
                  ) : undefined
                }
                value={
                  crewName
                    ? effectiveCrew?.crewRole
                      ? `${crewName} · ${formatCrewRole(effectiveCrew.crewRole)}`
                      : crewName
                    : null
                }
                placeholder="Select crew member"
                onClick={() => setCrewPickerOpen(true)}
                onClear={crewId ? () => setCrewId(undefined) : undefined}
              />
            </PrntrGroup>
          </RichSwitchGroup>
        </div>
      </div>

      {/* Footer — fixed bar on mobile, inline action row on desktop */}
      <div
        className={cn(
          isDesktop
            ? 'pt-2'
            : 'fixed bottom-0 left-0 right-0 bg-surface-L0 border-t border-border-L2 p-4 safe-bottom',
        )}
      >
        <div className={cn('flex gap-2', isDesktop && 'justify-end gap-3')}>
          <Button
            type="button"
            variant="secondary"
            className={cn(!isDesktop && 'flex-1')}
            onClick={() => navigate({ to: '/tasks' })}
          >
            Cancel
          </Button>
          {!hasChecklist ? (
            <Button
              type="button"
              variant="primary"
              className={cn(!isDesktop && 'flex-1')}
              disabled
            >
              Start
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="primary"
                className={cn(!isDesktop && 'flex-1')}
                onClick={handleAssignTask}
                disabled={isPending}
              >
                {isPending ? 'Assigning...' : 'Assign Task'}
              </Button>
              {isSelfAssignment && (
                <Button
                  type="button"
                  variant="primary"
                  className={cn(!isDesktop && 'flex-1')}
                  onClick={handleStartTask}
                  disabled={isPending}
                >
                  {isPending ? 'Starting...' : 'Start Task'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <ChecklistPickerDrawer
        isOpen={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        onSelect={(id) => setTemplateId(id)}
      />

      <AircraftPickerDrawer
        isOpen={aircraftPickerOpen}
        onClose={() => setAircraftPickerOpen(false)}
        onSelect={(id) => setAircraftId(id)}
        selectedAircraftId={aircraftId}
      />

      <CrewMemberPickerDrawer
        isOpen={crewPickerOpen}
        onClose={() => setCrewPickerOpen(false)}
        onSelect={(id) => setCrewId(id)}
        selectedCrewId={crewId ?? me?.id}
      />
    </>
  )

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/tasks' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to checklists"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/tasks" className="hover:underline">
          Checklists
        </Link>
        <span className="text-content-primary-subtle"> / Start or Assign Task</span>
      </span>
    </span>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Start or Assign Task" breadcrumb={breadcrumb} />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Start or Assign Task" />} className="pt-6">
      {body}
    </AppLayout>
  )
}

function RichSwitchGroup({
  title,
  caption,
  checked,
  onCheckedChange,
  children,
}: {
  title: string
  caption: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  children: React.ReactNode
}) {
  return (
    <Card className="flex flex-col gap-2 p-3">
      <div className="flex items-start gap-2">
        <div className="flex flex-col min-w-0 flex-1">
          <p className="text-sm font-semibold text-content-primary-bold leading-5">{title}</p>
          <p className="text-xs text-content-primary-subtle leading-[1.5]">{caption}</p>
        </div>
        <Switch checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5 shrink-0" />
      </div>
      {checked && children}
    </Card>
  )
}

